// ABOUTME: SPEC-67 D-9 — the reconnect handshake's lifecycle: a same-page reconnect holds `ws.onopen`
// ABOUTME: until the backend answers `session_resume`, then keeps the screen or runs today's reset.
//
// WHY a hook (incident 2026-09-25, ophion prod): a dropped connection threw the guest off the Vélos
// stage onto the reel and re-sent `intro_finished`, so the backend greeted them again — RC-2. The
// owner's rule: a drop is the same visit (same screen, no welcome); a refresh is a new one. App.js's
// WebSocket lifecycle is locked, so the whole handshake lives here and App.js carries only the call
// sites the spec names. Refs only: nothing here re-renders App.
//
// Contract with App.js:
//   • `classify(sid)` at connect → `resume=1` on the URL only for `same_page`.
//   • `deferOpen(sid)` in `onopen`, after `clearPending()`: true = return now and wait for the answer.
//   • `onAnswer(msg)` for `session_resume`; `onWaiting()` for a WAITING_ROOM frame; `onClose(code)`
//     in `onclose`; `onFreshSession()` on force_reconnect.
//   • `resumed:false`, a malformed answer or no answer in RESUME_ANSWER_TIMEOUT_MS (after a
//     WAITING_ROOM frame: WAITING_ROOM_INTERVAL_MS + RESUME_ANSWER_TIMEOUT_MS from the last one) → `reopen()`
//     re-invokes `onopen` with the guard released, so today's lines run in place, exactly as today
//     (an old backend never sends the frame, so it always lands here).
//     If the film ended DURING the wait, its `intro_finished` already went out on the open socket;
//     today's relaunch arm in `onopen` would reset `introFinishedSentRef` and ask for a second
//     welcome, so the release restores that ref after `reopen()` (review LOW, 2026-09-25). A ref
//     that was already true when the wait began is left to `onopen` exactly as today.
//   • `resumed:true` → the backend's stage goes through `hydrate` (the queues were already cleared
//     by `onopen`'s unconditional `clearPending()`), the grid context is re-sent, and
//     `intro_finished` is sent ONLY if one is banked (the 2026-09-08 "deaf agent" guarantee: a film
//     that ended while the socket was down is always reported).

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AUTH_CLOSE, OpenKind, RESUME_ANSWER_TIMEOUT_MS, WAITING_ROOM_INTERVAL_MS, classifyOpen, parseResumeAnswer,
} from './sessionResume';

/** Today's `onopen` waits this long before a banked `intro_finished`, so hydration lands first. */
const BANKED_INTRO_DELAY_MS = 500;

/**
 * The resumed branch: keep the screen, re-align it with the server's stage, and deliver only what
 * the dead socket owed. Never re-welcomes: `introFinishedSentRef` is untouched and the grid-empty
 * relaunch arm of today's `onopen` is deliberately absent. `onopen` already ran `clearPending()`.
 */
function applyResumed(answer, deps) {
  const { stageMachine, sendInteraction, sendGridContext, sendIntroFinished } = deps;
  if (stageMachine.hydrate(answer.stage) === 'restore') sendInteraction('stage_restore', {});
  const products = deps.productsRef?.current;
  if (products && products.length) sendGridContext(products);
  if (!deps.showIntroRef?.current && deps.introFinishedPendingRef?.current) {
    setTimeout(() => { if (!deps.showIntroRef?.current) sendIntroFinished(); }, BANKED_INTRO_DELAY_MS);
  }
}

/**
 * @param {{ isExperienceStarted: boolean, reopen: () => void, deps: object }} args
 *   deps = { stageMachine, sendInteraction, sendGridContext, sendIntroFinished, productsRef,
 *            showIntroRef, introFinishedPendingRef, introFinishedSentRef }
 */
export function useSessionResume({ isExperienceStarted, reopen, deps }) {
  const openedSidRef = useRef(null);   // the sid of this page's last socket that opened
  const pendingRef = useRef(null);     // { sid, timer, sentAtWait } while an open waits
  const releasingRef = useRef(false);  // true only while `reopen()` re-runs onopen
  const reopenRef = useRef(reopen);
  reopenRef.current = reopen;
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const cancel = useCallback(() => {
    if (!pendingRef.current) return;
    clearTimeout(pendingRef.current.timer);
    pendingRef.current = null;
  }, []);

  const release = useCallback((why) => {
    const sentRef = depsRef.current?.introFinishedSentRef;
    const sentDuringWait = pendingRef.current?.sentAtWait === false && !!sentRef?.current;
    cancel();
    console.log(`[SPEC-67][RESUME] outcome=fresh why=${why}`);
    releasingRef.current = true;
    try { reopenRef.current?.(); } finally { releasingRef.current = false; }
    // The film's welcome already went out during the wait; keep onopen's relaunch arm from re-asking.
    if (sentDuringWait) sentRef.current = true;
  }, [cancel]);

  const arm = useCallback((sid, sentAtWait, ms = RESUME_ANSWER_TIMEOUT_MS) => {
    cancel();
    const timer = setTimeout(() => release('timeout'), ms);
    pendingRef.current = { sid, timer, sentAtWait };
  }, [cancel, release]);

  const onFreshSession = useCallback(() => {
    cancel();
    openedSidRef.current = null;
  }, [cancel]);

  // Logout, the OTP gate or a 1008 all end the experience: the next entry is a fresh visit.
  useEffect(() => { if (!isExperienceStarted) onFreshSession(); }, [isExperienceStarted, onFreshSession]);
  useEffect(() => cancel, [cancel]);

  const classify = useCallback((sid) => classifyOpen({ openedSid: openedSidRef.current, sid }), []);

  const deferOpen = useCallback((sid) => {
    if (releasingRef.current) return false;
    // A second `open` for the socket already waiting is the same open, not a new wait.
    if (pendingRef.current && pendingRef.current.sid === sid) return true;
    const kind = classify(sid);
    openedSidRef.current = sid || null;
    if (kind !== OpenKind.SAME_PAGE) return false;
    arm(sid, !!depsRef.current?.introFinishedSentRef?.current);
    return true;
  }, [classify, arm]);

  const onAnswer = useCallback((msg) => {
    if (!pendingRef.current) {
      console.log('[SPEC-67][RESUME] outcome=ignored why=no_open_waiting');
      return;
    }
    const answer = parseResumeAnswer(msg);
    if (!answer.resumed) { release(`answer:${answer.reason}`); return; }
    cancel();
    console.log(`[SPEC-67][RESUME] outcome=resumed reason=${answer.reason} stage=${answer.stage?.stage || 'none'}`);
    applyResumed(answer, depsRef.current);
  }, [cancel, release]);

  const onWaiting = useCallback(() => {
    if (!pendingRef.current) return;
    // Queued: the next frame is due in WAITING_ROOM_INTERVAL_MS, so wait past it before falling back.
    arm(pendingRef.current.sid, pendingRef.current.sentAtWait, WAITING_ROOM_INTERVAL_MS + RESUME_ANSWER_TIMEOUT_MS);
  }, [arm]);

  const onClose = useCallback((code) => {
    cancel();
    if (code === AUTH_CLOSE) openedSidRef.current = null;
  }, [cancel]);

  return useMemo(() => ({
    classify, deferOpen, onAnswer, onWaiting, onClose, onFreshSession,
  }), [classify, deferOpen, onAnswer, onWaiting, onClose, onFreshSession]);
}
